import * as React from 'react';
import { useState, useEffect } from "react";
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import './MultiDropdownStyles.css';
import { VariableSizeList } from 'react-window';
import { useMediaQuery, useTheme } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';

const LISTBOX_PADDING = 4;

function renderRow(props) {
    const { data, index, style } = props;
    const item = data[index];
    const inlineStyle = {
        ...style,
        top: style.top + LISTBOX_PADDING,
        width: '100%',
        boxSizing: 'border-box',
        padding: '8px 16px',
        // overflow: 'hidden',
        whiteSpace: 'normal', // allow wrapping
    };
    return React.cloneElement(item, {
        style: inlineStyle,
    });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
    const outerProps = React.useContext(OuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
    const ref = React.useRef(null);
    React.useEffect(() => {
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [data]);
    return ref;
}

const StyledUl = React.forwardRef(({ style, ...props }, ref) => (
    <ul
        ref={ref}
        style={{
            ...style,
            width: '80%',
            listStyle: 'none',
            overflowY: 'auto',
        }}
        {...props}
    />
));

const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
    const { children, showApply, onApply, ...other } = props;
    const itemData = React.Children.toArray(children);
    const itemCount = itemData.length;
    const itemSize = 48;
    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'));

    const getChildSize = (child) => {
        const text = child?.props?.children?.[1] || "";
        const approxLines = Math.ceil(text.length / 20);
        return 52;
    };

    const gridRef = useResetCache(itemCount);

    return (
        <div ref={ref} style={{ width: '100%' }}>
            <OuterElementContext.Provider value={other} >
                <VariableSizeList
                    itemData={itemData}
                    height={200}
                    width="100%"
                    ref={gridRef}
                    outerElementType={OuterElementType}
                    innerElementType={StyledUl}
                    itemSize={(index) => getChildSize(itemData[index])}
                    overscanCount={5}
                    itemCount={itemCount}
                >
                    {renderRow}
                </VariableSizeList>
            </OuterElementContext.Provider>
            {showApply && (
                <div style={{ padding: '8px 12px', display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        size="small"
                        variant="contained"
                        sx={{ color: "#ffffff" }}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={onApply}
                    >
                        Apply
                    </Button>
                </div>
            )}
        </div>
    );
});

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

export const MultiDropdown = ({ id, options, onChangeItems, label, selectedItemsData, dropdownWidth, dropdownHeight, name, disabled, onApplyItems, showApply, onOpenDropdown }) => {
    const [selectedItems, setSelectedItems] = useState(selectedItemsData || []);
    const [openModal, setOpenModal] = useState(false);
    const [selectAll, setSelectAll] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(false);

    useEffect(() => {
        setSelectedItems(selectedItemsData || []);
        setSelectAll((selectedItemsData || []).length === options?.length);
    }, [selectedItemsData, options?.length]);
    const handleChange = (event, value, reason) => {

        if (value?.some((item) => item?.title === "Select All")) {
            if (selectAll) {
                setSelectedItems([]);
                setSelectAll(false);
            } else {
                setSelectedItems(options);
                setSelectAll(true);
            }
        } else {
            setSelectedItems(value);
            setSelectAll(value?.length === options?.length);
        }

        onChangeItems(name, value);
        if (showApply && Array.isArray(value) && value.length === 0) {
            if (typeof onApplyItems === "function") {
                onApplyItems(name, []);
            }
        }
    };
    const handleRemoveChip = (chipToRemove) => {
        const newSelectedItems = selectedItems?.filter(
            (item) => item.title !== chipToRemove?.title
        );
        setSelectedItems(newSelectedItems);
        setSelectAll(false);
        onChangeItems(name, newSelectedItems);
        if (showApply && newSelectedItems.length === 0) {
            if (typeof onApplyItems === "function") {
                onApplyItems(name, []);
            }
        }
    };

    const handleClickInside = () => {
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleSelectAllToggle = () => {
        const newSelectAll = !selectAll;
        setSelectAll(newSelectAll);
        const newSelectedItems = newSelectAll ? options : [];
        setSelectedItems(newSelectedItems);
        onChangeItems(name, newSelectedItems);
        if (selectAll) {
            setSelectedItems([]);
        } else {
            setSelectedItems(options);
        }
        setSelectAll(!selectAll);
    };

    const handleCloseDropdown = () => {
        setOpenDropdown(false);
        if (showApply) {
            return;
        }
        setSelectedItems(selectedItemsData || []);
        setSelectAll((selectedItemsData || []).length === options?.length);
    }
    const allOptions = options ? [{ title: "Select All", value: true }, ...options] : [];

    const handlePasteFromClipboard = async () => {
        try {
            const text = await navigator.clipboard.readText();
            const pastedValues = text
                .split(/[\n,]+/)
                .map(val => val.trim())
                .filter(Boolean);

            const matchedOptions = options.filter(option => {
                const [numberPart] = option.title.split('|');
                return pastedValues.includes(option.title) || pastedValues.includes(numberPart);
            });

            if (matchedOptions.length > 0) {
                setSelectedItems(matchedOptions);
                setSelectAll(matchedOptions.length === options.length);
                onChangeItems(name, matchedOptions);
            } else {
            }
        } catch (err) {
            console.error("Failed to read clipboard: ", err);
        }
    };

    const handleApply = () => {
        if (typeof onApplyItems === "function") {
            onApplyItems(name, selectedItems);
        }
        setOpenModal(false);
        setOpenDropdown(false);
    };

    return (
        <div>
            <Autocomplete
                ListboxComponent={ListboxComponent}
                ListboxProps={{ showApply, onApply: handleApply }}
                multiple={true}
                open={openDropdown}
                onOpen={() => {
                    setOpenDropdown(true);
                    if (typeof onOpenDropdown === "function") {
                        onOpenDropdown(name);
                    }
                }}
                onClose={handleCloseDropdown}
                disabled={disabled || false}
                id={id || "checkboxes-tags-demo"}
                name={name || "autoComplete_name"}
                options={allOptions}
                disableCloseOnSelect
                value={selectedItems}
                onChange={handleChange}
                isOptionEqualToValue={(option, value) => option?.title === value?.title}
                getOptionLabel={(option) => option?.title}
                filterOptions={(options, { inputValue }) => {
                    let filtered = options.filter(
                        option =>
                            option.title.toLowerCase().includes(inputValue.toLowerCase()) ||
                            (option.name && option.name.toLowerCase().includes(inputValue.toLowerCase()))
                    );

                    // if(!inputValue){
                    //     return filtered.slice(0,10);
                    // }
                    return filtered;
                }
                }
                renderOption={(props, option, { selected }) => {
                    const { key, ...optionProps } = props;
                    const isItemSelected = selectedItems?.some(item => item?.title === option?.title);
                    if (option?.title === 'Select All') {
                        return (
                            <li key={key} {...optionProps} onClick={handleSelectAllToggle}>
                                <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={selectAll}
                                />
                                {option?.title}
                            </li>
                        );
                    }

                    return (
                        <li key={key} {...optionProps} >
                            <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                style={{ marginRight: 8 }}
                                checked={isItemSelected}
                            />
                            {option?.title}
                        </li>
                    );
                }}
                style={{ maxWidth: dropdownWidth || "100%" }}
                renderInput={(params) => <TextField {...params} disabled={disabled || false} label={label} placeholder="Search" onClick={() => {
                    setSelectAll(selectedItems?.length === options?.length)

                }} />}
                renderTags={(selected, getTagProps) => {
                    if (selectedItemsData?.length > 0 && selectedItems?.length > 0) {
                        return (
                            <Box
                                onClick={handleClickInside}
                                style={{
                                    // height: "30px",
                                    width: "100%",
                                    display: 'flex',
                                    // overflowY: "scroll",
                                    flexWrap: "wrap",
                                    // gap: "4px",
                                    // padding: "8px",
                                    // border: "1px solid #ccc",
                                    borderRadius: "4px",
                                    transition: "all 0.3s ease",
                                    cursor: "pointer",
                                    backgroundColor: "white",
                                    maxWidth: "50px"
                                }}
                            >
                                {/* {selected?.map((option, index) => (
                                    <Chip
                                        label={option?.title}
                                        {...getTagProps({ index })}
                                        key={index}
                                        onDelete={() => handleRemoveChip(option)}
                                    />
                                ))} */}
                                <span>{selected?.length === 1 ?
                                    <span
                                        style={{
                                            maxWidth: "50px",      // restrict width
                                            overflow: "hidden",    // hide overflow
                                            textOverflow: "ellipsis", // show "..."
                                            whiteSpace: "nowrap",  // prevent wrapping
                                            display: "inline-block",
                                            verticalAlign: "middle"
                                        }}
                                    >{`${selected?.[0]?.title}`}</span> : `${selected?.length}+`}</span>
                            </Box>
                        )
                    } else {
                        return null;
                    }
                }}
            />
            <DialogActions className='pasteClipboard'>
                <Button className='clipboardButton' onClick={handlePasteFromClipboard} color="secondary">Paste from Clipboard</Button>
            </DialogActions>
            <Dialog open={openModal} onClose={handleCloseModal}>
                <DialogTitle>Selected Options ({selectedItems?.length})</DialogTitle>
                <DialogContent sx={{ maxHeight: "300px", overflowY: "auto" }}>
                    {selectedItems?.length > 0 ? (
                        selectedItems?.map((option, index) => (
                            <Chip
                                key={index}
                                label={option.title}
                                sx={{ margin: "4px" }}
                                onDelete={() => handleRemoveChip(option)}
                            />
                        ))
                    ) : (
                        <p>No items selected</p>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} color="primary">Close</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};
